import {
  Box,
  Button,
  Text,
  UnorderedList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { ContactRow } from "components/elements/ContactRow";
import MembersField, { MemberOption } from "components/elements/MembersField";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";
import { getCustomButtonVariant } from "utils/uiconfighelper";

const BorrowerContactsModal = ({
  borrower,
  isOpen,
  onClose,
  loadBorrower,
}: {
  borrower: any;
  isOpen: boolean;
  onClose: () => void;
  loadBorrower: () => void;
}) => {
  const { user } = useTenant();
  const { showSuccess, showError } = useContext(AlertsContext);

  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    setContacts(borrower.contacts);
  }, [borrower]);

  async function assignLoan() {
    try {
      if (members.length === 0) {
        onClose();
        return;
      }
      setLoading(true);
      await apiRequest("/supabase/borrower_contacts/insert", {
        borrower: borrower.id,
        users: members.map(member => member.value),
      });

      loadBorrower();
      onClose();
      setMembers([]);
      showSuccess({
        title: "Contact Added",
        description: "The contact has been assigned to the borrower.",
      });
    } catch (err) {
      showError({
        title: "Error Occured",
        description: getErrorMessage(err),
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function removeMember(id: string) {
    try {
      await apiRequest("/supabase/borrower_contacts/delete", {
        id,
      });

      loadBorrower();
      showSuccess({
        title: "Contact Removed",
        description: "The contact has been removed from the borrower.",
      });
    } catch (err) {
      showError({
        description: getErrorMessage(err),
      });
    }
  }

  const canRemoveContact = (contact: any) => {
    // Can't remove yourself
    if (contact.user.id === user.id) return false;

    // Admins can remove anyone
    if (user.role?.name === "Administrator") return true;

    // Lenders can remove contacts
    if (user.type === "lender") return true;

    // Borrowers can't remove contacts
    return false;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent borderRadius="20px">
        <ModalHeader pb="10px">Manage Contacts</ModalHeader>
        <ModalCloseButton />
        <ModalBody pt="0px">
          <Text fontSize="14px" lineHeight="24px" mb="12px">
            Manage borrower contacts.
          </Text>
          <UnorderedList listStyleType="none" p="0px" m="0px">
            {contacts.map((contact: any) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                editable={canRemoveContact(contact)}
                removeMember={removeMember}
              />
            ))}
          </UnorderedList>
          <Box mt="20px">
            <MembersField
              isMulti
              members={members}
              setMembers={setMembers}
              exclude={contacts.map((member: any) => member.user.id)}
              types={["lender", "borrower"]}
            />
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button variant={getCustomButtonVariant(user)} onClick={assignLoan} isLoading={loading}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BorrowerContactsModal;
